/**
 * What the person should do next, from the Quality overview: a card's "What to do" line and its one primary action
 * (`whatToDo`), and the ordered "What to do next" list at the top of the overview (`nextSteps`), worst first. Pure:
 * the components render these and map the action kinds to their handlers:
 *
 * - `fix`: "Fix with AI" (the card's prompt to the project chat);
 * - `lockfiles`: "Create lockfiles" (POST /quality/lockfiles);
 * - `writeTests`: "Write tests with AI";
 * - `runA11y`: run the accessibility check in this browser;
 * - `run`: run this check; `runAll`: run every check;
 * - `open`: open the check's page; `settings`: open the settings.
 */
import { CHECKS, plural, statusOf } from './qualityModel';

const ACTIONS = {
  fix: 'Fix with AI',
  lockfiles: 'Create lockfiles',
  writeTests: 'Write tests with AI',
  runA11y: 'Check the pages',
  run: 'Run it',
  runAll: 'Run all checks',
  open: 'Open',
  settings: 'Review rules',
};
const action = (kind, label = ACTIONS[kind]) => ({ kind, label });

const ruleOf = (gate, id) => (gate?.rules || []).find((r) => r.id === id) || null;
const live = (card) => card?.findingsCount ?? (card?.top || []).length;
const names = (list, max = 3) => (list.length > max ? `${list.slice(0, max).join(', ')} and ${list.length - max} more` : list.join(', '));

/** The services without a lockfile, from the dependencies card (its details or its top findings), else vulnerabilities. */
export function locklessServices(checks = {}) {
  const d = checks.dependencies?.details || {};
  if (Array.isArray(d.noLockfile) && d.noLockfile.length) { return d.noLockfile; }
  const fromFindings = (checks.dependencies?.top || []).filter((f) => f.rule === 'no-lockfile').map((f) => f.service).filter(Boolean);
  if (fromFindings.length) { return fromFindings; }
  const v = checks.vulnerabilities?.details || {};
  return Array.isArray(v.noLockfile) ? v.noLockfile : [];
}

/**
 * Whether a missing lockfile blocks Publish: a service with a package manifest and no lockfile is a gap of the gate
 * (vfs-service gaps.js, kind `vulnerabilities`), unless it was waived with a reason. Nothing to lock is no gap.
 */
export const lockfileBlocks = (gate, card = null) => [...(gate?.gaps || []), ...(card?.gaps || [])].some((gap) => gap.kind === 'vulnerabilities' && gap.blocking);

/** Whether the project has no test at all (the tests card ran and found none). */
export const hasNoTests = (card) => Boolean(card && card.ranAt && !card.counts?.total && ['info', 'unsupported', 'cant_run'].includes(card.status));

/**
 * A card's "What to do": `{ text, action }` (action may be null); null when nothing is to do (passing, or no answer
 * that needs one).
 */
export function whatToDo(id, card, { gate = null, checks = {} } = {}) {
  if (!card) { return null; }
  const status = card.status;
  if (status === 'skipped' && !card.ranAt) {
    if (id === 'accessibility') { return { text: 'Not checked yet: it runs in your browser on the running preview.', action: action('runA11y') }; }
    return { text: 'Not run yet.', action: action('run') };
  }
  // Not needed (code scanning with nothing for Semgrep or Hadolint): an answer, nothing to do.
  if (status === 'not_needed') { return null; }
  if (status === 'error') { return { text: `It could not run: ${card.message || card.summary || 'see the details'}.`, action: action('run', 'Run again') }; }
  if (status === 'not_ready') { return { text: card.sub || card.summary || 'Start the service once so its dependencies are installed.', action: action('open') }; }
  if (status === 'offline') { return { text: card.summary || 'Online lookups are off or unreachable.', action: action('settings', 'Settings') }; }
  if (id === 'tests' && hasNoTests(card)) {
    const rule = ruleOf(gate, 'failingTests');
    const blocks = rule && rule.status === 'fail';
    if (card.status === 'cant_run') {
      return { text: `The tests can't run here: ${card.sub || card.summary || 'a toolchain is missing'}. ${blocks ? 'A missing check never counts as passing, so this blocks Publish.' : ''}`.trim(), action: action('open') };
    }
    return {
      text: `No tests yet. ${blocks ? 'A missing check never counts as passing, so this blocks Publish.' : 'Nothing checks that the app keeps working.'} Add a test setup and a first test per service, or waive it in Settings with a reason.`,
      action: action('writeTests'),
    };
  }
  const blocksForLockfile = ['dependencies', 'vulnerabilities'].includes(id) && lockfileBlocks(gate, card);
  if (card.unchecked || blocksForLockfile || (id === 'dependencies' && locklessServices(checks).length && !card.counts?.packages)) {
    if (['dependencies', 'vulnerabilities', 'sbom'].includes(id)) {
      const missing = locklessServices(checks);
      if (!missing.length && !blocksForLockfile) { return card.summary ? { text: card.summary, action: null } : null; }
      const then = blocksForLockfile
        ? 'A missing check never counts as passing, so this blocks Publish: create lockfiles, or waive it in Settings with a reason.'
        : 'Create lockfiles, then run again.';
      return { text: `No lockfile${missing.length ? ` in ${names(missing)}` : ''}: the packages can't be checked. ${then}`, action: action('lockfiles') };
    }
  }
  if (id === 'accessibility' && status === 'skipped') { return { text: card.summary || 'Start a web service\'s preview, then check its pages.', action: action('runA11y') }; }
  if (status === 'pass') { return null; }
  if (status === 'info') {
    if (id === 'codeStats') { return { text: 'For your information: it never blocks Publish. Set limits in Settings to be warned.', action: action('open') }; }
    if (id === 'apiContract') { return { text: card.summary || 'No endpoint found yet.', action: action('open') }; }
    if (id === 'performance') { return { text: 'Nothing measured yet: build a web service, or use the app so Traces sees requests.', action: action('run', 'Measure again') }; }
    return card.summary ? { text: card.summary, action: action('open') } : null;
  }
  // fail / warn
  const texts = {
    tests: 'Some tests fail: fix the code (or the test if it checks the wrong thing).',
    readiness: 'Fix what works only in the preview, so the app also works once published.',
    lint: 'Fix the lint errors; many can be fixed automatically.',
    secrets: 'Move the keys out of the code into the project secrets, and rotate them.',
    security: 'Fix the risky code or configuration.',
    vulnerabilities: 'Upgrade the packages with a fix; replace the ones without.',
    dependencies: 'Replace the packages with a licence not allowed, or allow the licence if your company accepts it.',
    privacy: 'Stop writing personal data to the logs, and give personal data a way to be deleted.',
    apiContract: 'Endpoints that others may call changed: keep the old ones, or agree the change with whoever calls them.',
    performance: 'Make the heavy pages lighter and the slow routes faster, or change the budgets.',
    accessibility: 'Fix the elements listed so everyone can use the pages.',
    codeStats: 'Split the most complex functions and long files, or raise the limits in Settings.',
  };
  const fixable = ['tests', 'readiness', 'lint', 'secrets', 'security', 'vulnerabilities', 'privacy', 'performance', 'accessibility'].includes(id) && (card.top || []).length > 0;
  return { text: texts[id] || card.summary || 'See the details.', action: fixable ? action('fix') : action('open') };
}

const RANK = { fail: 0, warn: 1, info: 2 };

/**
 * The ordered next steps: `[{ key, tone, title, detail, check, action, blocks }]`, worst first (what blocks Publish,
 * then what fails, then the lockfiles, then what needs attention, then the missing tests and what was not checked).
 * Empty: all clear.
 */
export function nextSteps(overview) {
  if (!overview) { return []; }
  const checks = overview.checks || {};
  const gate = overview.gate || {};
  const steps = [];
  if (gate.neverRan || !overview.run) {
    return [{ key: 'never', tone: 'info', title: 'Not checked yet', detail: 'Run all checks to see where the project stands and what would block Publish.', check: null, action: action('runAll'), blocks: false }];
  }
  const covered = new Set();
  // 1. What blocks Publish.
  (gate.rules || []).filter((r) => r.status === 'fail').forEach((rule) => {
    covered.add(rule.check);
    if (rule.id === 'readiness') {
      const blocking = (rule.items || []).filter((i) => i.blocks);
      steps.push({ key: `gate-${rule.id}`, tone: 'fail', rank: 0, title: `${plural(blocking.length || 1, 'thing')} would break the app in production`, detail: rule.detail, check: 'readiness', action: action('fix'), blocks: !gate.override });
      return;
    }
    if (rule.missing) {
      // A missing check (no tests, no coverage report, tests that can't run): its gap label first, never "fix the code".
      const kind = rule.gap === 'No tests' ? action('writeTests') : rule.gap === 'No coverage report' ? action('fix', 'Set up coverage with AI') : rule.gap === 'No lockfile' ? action('lockfiles') : action('open');
      steps.push({ key: `gate-${rule.id}`, tone: 'fail', rank: 0, title: `Missing: ${rule.gap || rule.label}`, detail: rule.detail, check: rule.check, action: kind, blocks: !gate.override, missing: true });
      return;
    }
    steps.push({ key: `gate-${rule.id}`, tone: 'fail', rank: 0, title: rule.label, detail: rule.detail, check: rule.check, action: ['tests', 'vulnerabilities', 'secrets', 'lint', 'security', 'privacy', 'accessibility', 'performance'].includes(rule.check) ? action('fix') : action('open'), blocks: !gate.override });
  });
  // 2. Lockfiles: one step for dependencies, vulnerabilities and the SBOM (already listed when it blocks Publish).
  const missing = locklessServices(checks);
  const lockfileRule = (gate.rules || []).some((r) => r.status === 'fail' && r.missing && r.gap === 'No lockfile');
  if (missing.length && lockfileRule) {
    ['dependencies', 'vulnerabilities', 'sbom'].forEach((id) => covered.add(id));
  } else if (missing.length) {
    ['dependencies', 'vulnerabilities', 'sbom'].forEach((id) => covered.add(id));
    steps.push({ key: 'lockfiles', tone: 'warn', rank: 1.5, title: `Dependencies can't be checked: no lockfile in ${plural(missing.length, 'service')}`, detail: `${names(missing)}: without a lockfile, licences, vulnerabilities and the SBOM have nothing to check.`, check: 'dependencies', action: action('lockfiles'), blocks: false });
  }
  // 3. Readiness items that do not block (they still need fixing before production).
  const readiness = checks.readiness;
  if (readiness && !covered.has('readiness') && ['fail', 'warn'].includes(readiness.status) && live(readiness)) {
    covered.add('readiness');
    steps.push({ key: 'readiness', tone: statusOf(readiness.status).tone, rank: RANK[statusOf(readiness.status).tone] + 0.5, title: `${plural(live(readiness), 'production readiness item')}`, detail: readiness.summary, check: 'readiness', action: action('open'), blocks: false });
  }
  // 4. The other failing or attention cards.
  Object.entries(checks).forEach(([id, card]) => {
    if (covered.has(id) || !CHECKS[id]) { return; }
    const tone = statusOf(card.status).tone;
    if (!['fail', 'warn'].includes(tone)) { return; }
    const todo = whatToDo(id, card, { gate, checks });
    steps.push({ key: id, tone, rank: RANK[tone] + 1, title: `${CHECKS[id].title}: ${card.metric || statusOf(card.status).label}`, detail: todo?.text || card.summary, check: id, action: todo?.action || action('open'), blocks: false });
    covered.add(id);
  });
  // 5. No tests at all.
  if (!covered.has('tests') && hasNoTests(checks.tests)) {
    const services = (overview.detected?.services || []).map((s) => s.service);
    steps.push({ key: 'tests', tone: 'info', rank: 3, title: services.length > 1 ? 'No tests in any service' : 'No tests yet', detail: whatToDo('tests', checks.tests, { gate, checks })?.text, check: 'tests', action: action('writeTests'), blocks: false });
  }
  // 6. Not checked: accessibility never ran, a check that could not run.
  Object.entries(checks).forEach(([id, card]) => {
    if (covered.has(id) || !CHECKS[id]) { return; }
    if (card.status === 'error') {
      steps.push({ key: `error-${id}`, tone: 'warn', rank: 2.5, title: `${CHECKS[id].title} could not run`, detail: card.message || card.summary, check: id, action: action('run', 'Run again'), blocks: false });
    } else if (id === 'accessibility' && card.status === 'skipped') {
      steps.push({ key: 'a11y', tone: 'info', rank: 4, title: 'Accessibility not checked yet', detail: card.ranAt ? card.summary : 'It runs in your browser, on the running preview of each web service.', check: id, action: action('runA11y'), blocks: false });
    }
  });
  steps.sort((a, b) => a.rank - b.rank);
  if (gate.stale) {
    steps.unshift({ key: 'stale', tone: 'info', rank: -1, title: 'The code changed since the last run', detail: 'Run the checks again for a fresh answer.', check: null, action: action('runAll', 'Run again'), blocks: false });
  }
  return steps.map((step) => {
    const out = { ...step };
    delete out.rank;
    return out;
  });
}

/** Whether a card shows for a chip filter (`fail`, `warn`, `pass`, `info`, `prog`): its tone now. */
export function matchesFilter(tone, filter) {
  return !filter || tone === filter;
}
